/**
 * Central definitions for system roles used throughout the app.
 *
 * Hierarchy: external = skjerm < user < admin < superadmin
 */

/**
 * DB enum: admin | superadmin | user | external | skjerm | project_lead (legacy) |
 * employee (legacy) | kunderadgiver (legacy) | regnskapsforer (legacy)
 *
 * Active roles after VIB-548:
 * - superadmin: full access + kan impersonere
 * - admin: full access
 * - user: vanlig ansatt, ser egne prosjekter/oppgaver
 * - external: regnskap/revisor, begrenset tilgang
 * - skjerm: TV-skjerm (VIB-2712). Kun Display, ingenting annet. Finnes for at
 *   skjermen i resepsjonen ikke skal måtte stå innlogget på en admin-konto.
 * - motebooker: ringer kalde leads og booker møter (CRM-4.0). Ansatt-nivå (rang 1),
 *   så chat og egne varsler virker, men ser bare menynøkkelen salgsmuligheter.leads
 *   og får ikke tildelt produksjonsarbeid.
 *
 * MERK: typen brukes også som `minRole` i hjelp- og endringslogg-gating. `skjerm`
 * er rangert 0, så `minRole: "skjerm"` ville oppført seg som «alle» — det er
 * meningsløst, men harmløst. Bruk `"user"` når du mener «ikke eksterne».
 */
export type SystemRole = "superadmin" | "admin" | "user" | "external" | "skjerm" | "motebooker";

/**
 * Standard månedskapasitet (timer) når en bruker ikke har egen
 * `monthly_capacity_hours` satt. 7,5t × ~18,8 arbeidsdager. Skal være IDENTISK
 * med DB-kolonnens default (public.users.monthly_capacity_hours). DUP-M1:
 * tidligere hardkodet som 141.25 i ≥5 kildefiler — importer denne i stedet.
 */
export const DEFAULT_MONTHLY_CAPACITY_HOURS = 141.25;

export const DEFAULT_PROVISION_RATE_PCT = 5; // matcher system_config.provision_rate; fallback når config mangler

export const SYSTEM_ROLE_LABELS: Record<string, string> = {
  superadmin: "Superadmin",
  admin: "Admin / Leder",
  user: "Ansatt",
  external: "Ekstern",
  // VIB-2712: TV-skjerm i resepsjonen, ikke en person.
  skjerm: "Skjerm (TV)",
  // CRM-4.0: ringer kalde leads og booker kundemøter.
  motebooker: "Møtebooker",
  // Legacy mappings for data som fortsatt kan vises (ingen brukere har dem aktivt)
  employee: "Ansatt",
  kunderadgiver: "Kunderådgiver (legacy)",
  regnskapsforer: "Regnskapsfører (legacy)",
  project_lead: "Prosjektleder (legacy)",
};

/** Roles shown in dropdowns */
export const SYSTEM_ROLE_OPTIONS: SystemRole[] = [
  "superadmin",
  "admin",
  "user",
  "external",
  "skjerm",
  "motebooker",
];

export const ROLE_COLORS: Record<string, string> = {
  superadmin: "bg-fuchsia-100 text-fuchsia-700",
  admin: "bg-purple-100 text-purple-700",
  user: "bg-gray-100 text-gray-600",
  external: "bg-teal-100 text-teal-700",
  skjerm: "bg-slate-100 text-slate-600",
  motebooker: "bg-pink-100 text-pink-700",
  // Legacy
  employee: "bg-gray-100 text-gray-600",
  kunderadgiver: "bg-blue-100 text-blue-700",
  regnskapsforer: "bg-teal-100 text-teal-700",
  project_lead: "bg-gray-100 text-gray-600",
};

const ROLE_HIERARCHY: Record<string, number> = {
  external: 0,
  // VIB-2712: samme nivaa som external. Ser Display, ingenting annet.
  skjerm: 0,
  // CRM-4.0: ansatt-nivå så chat og egne varsler virker. Samme verdi som role_rank() i SQL.
  motebooker: 1,
  user: 1,
  admin: 2,
  superadmin: 3,
  // Legacy
  employee: 1,
  kunderadgiver: 1,
  project_lead: 1,
  regnskapsforer: 0,
};

export function hasMinRole(userRole: string, minRole: SystemRole): boolean {
  const userLevel = ROLE_HIERARCHY[userRole] ?? 0;
  const minLevel = ROLE_HIERARCHY[minRole] ?? 0;
  return userLevel >= minLevel;
}

/** Kun superadmin kan impersonere andre brukere (BLOCKER-5). */
export function canImpersonate(role: string | undefined): boolean {
  return hasMinRole(role ?? "user", "superadmin");
}

/**
 * VIB-1167: skille «ansatt» (produserende person) fra «konto» (innlogging).
 * Ansatt = admin/user (+ legacy employee/kunderadgiver/project_lead). Konto-
 * roller (superadmin = Vibbing/test, external/regnskapsforer = revisor) er IKKE
 * ansatte og skjules fra Ansatte-siden — de administreres på Brukere-siden.
 * Delt helper så Ansatte-lista og Brukere-siden ikke kan divergere.
 */
const EMPLOYEE_ROLES = new Set<string>([
  "admin",
  "user",
  // Legacy-roller behandlet som ansatt:
  "employee",
  "kunderadgiver",
  "project_lead",
]);

export function isEmployeeRole(role: string | null | undefined): boolean {
  return EMPLOYEE_ROLES.has(role ?? "");
}

export const SPECIALIZATION_LABELS: Record<string, string> = {
  producer: "Produsent",
  editor: "Klipper",
  photographer: "Fotograf",
  director: "Regissør",
  coordinator: "Koordinator",
  freelancer: "Frilanser",
};

export const SPECIALIZATION_OPTIONS = [
  "producer",
  "editor",
  "photographer",
  "director",
  "coordinator",
  "freelancer",
];

export const SPECIALIZATION_COLORS: Record<string, string> = {
  producer: "bg-blue-100 text-blue-700",
  editor: "bg-emerald-100 text-emerald-700",
  photographer: "bg-amber-100 text-amber-700",
  director: "bg-rose-100 text-rose-700",
  coordinator: "bg-cyan-100 text-cyan-700",
  freelancer: "bg-orange-100 text-orange-700",
};
